import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, InputGroup } from 'react-bootstrap';
import { businessApi } from '../services/api';
import { FiSettings, FiStar, FiLink, FiSave } from 'react-icons/fi';

const SettingsPage = () => {
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    googleReviewLink: '',
    reviewRequestEnabled: true
  });

  useEffect(() => {
    fetchBusinessProfile();
  }, []);

  const fetchBusinessProfile = async () => {
    try {
      setLoading(true);
      const response = await businessApi.getProfile();
      const businessData = response.data.data.business;
      setBusiness(businessData);
      setFormData({
        googleReviewLink: businessData.googleReviewLink || '',
        reviewRequestEnabled: businessData.reviewRequestEnabled !== false
      });
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load business profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await businessApi.updateProfile({
        googleReviewLink: formData.googleReviewLink,
        reviewRequestEnabled: formData.reviewRequestEnabled
      });
      setSuccess('Settings saved successfully!');
      await fetchBusinessProfile();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="loading-spinner" style={{ margin: '0 auto', width: '40px', height: '40px' }}></div>
        <p className="mt-3">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <h2 className="mb-4">
        <FiSettings className="me-2" />
        Settings
      </h2>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Card>
        <Card.Header className="bg-primary text-white">
          <h5 className="mb-0">
            <FiStar className="me-2" />
            Review Request Settings
          </h5>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-4">
              <Form.Label>
                <strong>Google Review Link</strong>
              </Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <FiLink />
                </InputGroup.Text>
                <Form.Control
                  type="url"
                  placeholder="https://g.page/r/..."
                  value={formData.googleReviewLink}
                  onChange={(e) => setFormData({ ...formData, googleReviewLink: e.target.value })}
                />
              </InputGroup>
              <Form.Text className="text-muted">
                Get your Google review link from your Google Business Profile. 
                Customers will receive an email with this link after their appointment is completed.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Check
                type="switch"
                id="reviewRequestEnabled"
                label="Enable Review Request Emails"
                checked={formData.reviewRequestEnabled}
                onChange={(e) => setFormData({ ...formData, reviewRequestEnabled: e.target.checked })}
              />
              <Form.Text className="text-muted">
                When enabled, customers will automatically receive a review request email 
                after their appointment is marked as completed.
              </Form.Text>
            </Form.Group>

            <Alert variant="info" className="mb-4">
              <strong>How it works:</strong>
              <ul className="mb-0 mt-2">
                <li>When you mark an appointment as "completed", a review request email is automatically sent</li>
                <li>The email includes a direct link to your Google Business Profile</li>
                <li>Review requests are only sent if you have a Google review link configured</li>
                <li>You can enable/disable this feature at any time</li>
              </ul>
            </Alert>

            <Button
              variant="primary"
              type="submit"
              disabled={saving}
            >
              <FiSave className="me-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      <Card className="mt-4 bg-light">
        <Card.Body>
          <h6>How to Get Your Google Review Link</h6>
          <ol>
            <li>Go to <a href="https://business.google.com" target="_blank" rel="noopener noreferrer">Google Business Profile</a></li>
            <li>Select your business</li>
            <li>Click on "Get more reviews" or "Share"</li>
            <li>Copy the review link</li>
            <li>Paste it in the field above</li>
          </ol>
          <p className="text-muted small mb-0">
            The link typically looks like: <code>https://g.page/r/...</code> or <code>https://maps.app.goo.gl/...</code>
          </p>
        </Card.Body>
      </Card>
    </div>
  );
};

export default SettingsPage;
